import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Copy, Loader2, X } from 'lucide-react';
import api from '@/lib/api';
import BreathingCircle from '@/components/BreathingCircle';

const MAX_ATTEMPTS = 12;
const POLL_MS = 2500;

export default function GiftSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get('session_id');
    const [state, setState] = useState('polling'); // polling | success | expired | error
    const [data, setData] = useState(null);
    const [copied, setCopied] = useState(false);
    const attemptsRef = useRef(0);

    useEffect(() => {
        if (!sessionId) { setState('error'); return; }
        let cancelled = false;
        const tick = async () => {
            try {
                const { data: d } = await api.get(`/gifts/status/${sessionId}`);
                if (cancelled) return;
                setData(d);
                if (d.payment_status === 'paid') { setState('success'); return; }
                if (d.status === 'expired') { setState('expired'); return; }
            } catch {
                if (cancelled) return;
            }
            attemptsRef.current += 1;
            if (attemptsRef.current >= MAX_ATTEMPTS) { setState('error'); return; }
            setTimeout(tick, POLL_MS);
        };
        tick();
        return () => { cancelled = true; };
    }, [sessionId]);

    const copy = async () => {
        if (!data?.gift_code) return;
        try {
            await navigator.clipboard.writeText(data.gift_code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    };

    return (
        <div className="space-y-8" data-testid="gift-success-page">
            <header>
                <p className="text-xs uppercase tracking-[0.25em] text-forest-soft">Gift</p>
                <h1 className="font-serif text-4xl text-forest mt-2">
                    {state === 'success' ? 'Beautifully done.' :
                     state === 'expired' ? 'That session timed out.' :
                     state === 'error' ? 'Something went sideways.' :
                     'Confirming with Stripe…'}
                </h1>
            </header>

            {state === 'polling' && (
                <div className="qm-card p-10 grid place-items-center" data-testid="gift-polling">
                    <BreathingCircle size={180} label="Confirming" />
                    <p className="text-sm text-forest-soft mt-6 text-center">
                        Stripe is letting us know the moment your payment clears. This usually takes a few seconds.
                    </p>
                </div>
            )}

            {state === 'success' && data && (
                <div className="qm-card p-7 md:p-9 space-y-6" data-testid="gift-result">
                    <div>
                        <p className="text-sm text-forest-soft">{data.package?.label} of premium · ${data.amount?.toFixed?.(2)}</p>
                        <p className="text-forest mt-3">
                            Here is the redemption code. Send it to {data.recipient_email ? <span className="font-medium">{data.recipient_email}</span> : 'whomever you like'}.
                        </p>
                    </div>
                    <div className="rounded-3xl bg-sage/25 p-6 flex items-center justify-between gap-4" data-testid="gift-code-box">
                        <div className="font-mono text-2xl md:text-3xl text-forest tracking-wider" data-testid="gift-code">{data.gift_code}</div>
                        <button onClick={copy} data-testid="gift-copy" className="qm-btn-primary inline-flex items-center gap-2">
                            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? 'Copied' : 'Copy code'}
                        </button>
                    </div>
                    {data.message && <p className="italic text-forest-soft">“{data.message}”</p>}
                    <p className="text-xs text-forest-soft">
                        The recipient enters this code on their <Link to="/app/profile" className="underline text-forest">profile</Link> to unlock premium for {data.package?.months} months.
                    </p>
                </div>
            )}

            {state === 'expired' && (
                <div className="qm-card p-8 flex items-start gap-4" data-testid="gift-expired">
                    <X className="text-forest-soft mt-1" />
                    <div>
                        <p className="text-forest font-medium">Checkout expired before payment.</p>
                        <p className="text-sm text-forest-soft mt-1">No charge was made. You can start again.</p>
                        <Link to="/app/gifts" className="qm-btn-primary inline-block mt-4">Back to gifts</Link>
                    </div>
                </div>
            )}

            {state === 'error' && (
                <div className="qm-card p-8 flex items-start gap-4" data-testid="gift-error">
                    <X className="text-forest-soft mt-1" />
                    <div>
                        <p className="text-forest font-medium">We couldn’t confirm the payment yet.</p>
                        <p className="text-sm text-forest-soft mt-1">If you completed it, the gift may still appear on your profile shortly. Otherwise just try again.</p>
                        <Link to="/app/gifts" className="qm-btn-primary inline-block mt-4">Back to gifts</Link>
                    </div>
                </div>
            )}
        </div>
    );
}
